/**
 * `createTextureProvider` (W2-01a preview-gl; contract render/types.ts `TextureProvider`, ARCHITECTURE.md §7.3).
 *
 * Project media for the renderer, decoded lazily on first request and cached until `dispose`:
 *  - `image`: straight-alpha ImageBitmap (`premultiplyAlpha: 'none'`, no colour-space conversion, EXIF orientation
 *    ignored like Phira's decoder); a GIF gives its first frame (a .gif texture without gif events);
 *  - `gif`: every frame composited (gifCache.ts: ImageDecoder, gifuct-js fallback) with Phira's raw delays from the
 *    injected `gifDelays` (AssetMeta.gifDelaysMs), read from the file when unknown;
 *  - `blurredIllustration`: Phira's background blur computed by the injected `blur` (io worker);
 *  - `shaderSource`: the UTF-8 text of a custom shader (Phira rejects other encodings: `failure` says why);
 *  - `video`: a muted, inline <video> element over a blob URL (P1 preview).
 * Every getter returns null while loading; when an item settles (loaded or failed) the `onReady` callbacks run so
 * the preview redraws. Failures are remembered (no retry loop) and readable through `failure(asset)`.
 */

import type { Unsubscribe } from '../../core/util/types';
import type { AssetId } from '../../core/model/types';
import type { CreateTextureProvider, DecodedGif, TextureProvider } from '../types';
import { closeGif, decodeGif } from './gifCache';

/** Longest side of the blurred illustration requested from the io worker (it is shown heavily blurred). */
export const BLUR_MAX_SIDE = 1024;

/** Straight-alpha decode options (Phira's image decoder: no premultiplication, no colour management, no EXIF). */
export const IMAGE_BITMAP_OPTIONS: ImageBitmapOptions = {
  premultiplyAlpha: 'none',
  colorSpaceConversion: 'none',
  imageOrientation: 'none',
};

type Kind = 'image' | 'gif' | 'blur' | 'shader' | 'video';

type Slot<T> =
  | { readonly state: 'loading' }
  | { readonly state: 'ready'; readonly value: T }
  | { readonly state: 'failed'; readonly reason: string };

function reasonOf(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

class LazyTextureProvider implements TextureProvider {
  private readonly deps: Parameters<CreateTextureProvider>[0];
  private readonly images = new Map<AssetId, Slot<ImageBitmap>>();
  private readonly gifs = new Map<AssetId, Slot<DecodedGif>>();
  private readonly blurred = new Map<AssetId, Slot<ImageBitmap>>();
  private readonly shaders = new Map<AssetId, Slot<string>>();
  private readonly videos = new Map<AssetId, Slot<HTMLVideoElement>>();
  private readonly videoUrls = new Map<AssetId, string>();
  private readonly bytesInFlight = new Map<AssetId, Promise<Uint8Array>>();
  private readonly listeners = new Set<() => void>();
  private disposed = false;

  constructor(deps: Parameters<CreateTextureProvider>[0]) {
    this.deps = deps;
  }

  private bytes(asset: AssetId): Promise<Uint8Array> {
    let p = this.bytesInFlight.get(asset);
    if (p === undefined) {
      p = this.deps.load(asset);
      this.bytesInFlight.set(asset, p);
      const drop = (): void => {
        this.bytesInFlight.delete(asset);
      };
      p.then(drop, drop);
    }
    return p;
  }

  private notify(): void {
    if (this.disposed) return;
    for (const cb of [...this.listeners]) cb();
  }

  /** Starts loading `asset` into `map` with `make` unless already known; returns the current slot. */
  private lazy<T>(
    map: Map<AssetId, Slot<T>>,
    asset: AssetId,
    make: (bytes: Uint8Array) => Promise<T>,
    discard: (v: T) => void,
  ): T | null {
    if (this.disposed) return null;
    const slot = map.get(asset);
    if (slot !== undefined) return slot.state === 'ready' ? slot.value : null;
    map.set(asset, { state: 'loading' });
    this.bytes(asset)
      .then(make)
      .then(
        (value) => {
          if (this.disposed || map.get(asset)?.state !== 'loading') {
            discard(value);
            return;
          }
          map.set(asset, { state: 'ready', value });
          this.notify();
        },
        (e: unknown) => {
          if (this.disposed) return;
          map.set(asset, { state: 'failed', reason: reasonOf(e) });
          this.notify();
        },
      );
    return null;
  }

  image(asset: AssetId): ImageBitmap | null {
    return this.lazy(
      this.images,
      asset,
      (bytes) => createImageBitmap(new Blob([bytes.slice()]), IMAGE_BITMAP_OPTIONS),
      (b) => {
        b.close();
      },
    );
  }

  gif(asset: AssetId): DecodedGif | null {
    return this.lazy(this.gifs, asset, (bytes) => decodeGif(bytes, this.deps.gifDelays(asset)), closeGif);
  }

  blurredIllustration(asset: AssetId): ImageBitmap | null {
    return this.lazy(
      this.blurred,
      asset,
      (bytes) => this.deps.blur(bytes, BLUR_MAX_SIDE),
      (b) => {
        b.close();
      },
    );
  }

  shaderSource(asset: AssetId): string | null {
    return this.lazy(
      this.shaders,
      asset,
      (bytes) => Promise.resolve(new TextDecoder('utf-8', { fatal: true }).decode(bytes)),
      () => undefined,
    );
  }

  video(asset: AssetId): HTMLVideoElement | null {
    return this.lazy(
      this.videos,
      asset,
      (bytes) => this.makeVideo(asset, bytes),
      (v) => {
        v.removeAttribute('src');
        v.load();
      },
    );
  }

  private makeVideo(asset: AssetId, bytes: Uint8Array): Promise<HTMLVideoElement> {
    if (typeof document === 'undefined' || typeof URL.createObjectURL !== 'function') {
      return Promise.reject(new Error('video playback is not available here'));
    }
    const v = document.createElement('video');
    v.muted = true;
    v.playsInline = true;
    v.preload = 'auto';
    v.loop = false;
    const url = URL.createObjectURL(new Blob([bytes.slice()]));
    this.videoUrls.set(asset, url);
    return new Promise<HTMLVideoElement>((resolve, reject) => {
      const done = (): void => {
        v.removeEventListener('loadeddata', onData);
        v.removeEventListener('error', onError);
      };
      const onData = (): void => {
        done();
        resolve(v);
      };
      const onError = (): void => {
        done();
        reject(new Error(v.error?.message ?? 'the browser cannot decode this video'));
      };
      v.addEventListener('loadeddata', onData);
      v.addEventListener('error', onError);
      v.src = url;
      v.load();
    });
  }

  failure(asset: AssetId, kind?: Kind): string | null {
    const maps: ReadonlyArray<readonly [Kind, Map<AssetId, Slot<unknown>>]> = [
      ['image', this.images],
      ['gif', this.gifs],
      ['blur', this.blurred],
      ['shader', this.shaders],
      ['video', this.videos],
    ];
    for (const [k, map] of maps) {
      if (kind !== undefined && k !== kind) continue;
      const s = map.get(asset);
      if (s?.state === 'failed') return s.reason;
    }
    return null;
  }

  onReady(cb: () => void): Unsubscribe {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const s of this.images.values()) if (s.state === 'ready') s.value.close();
    for (const s of this.blurred.values()) if (s.state === 'ready') s.value.close();
    for (const s of this.gifs.values()) if (s.state === 'ready') closeGif(s.value);
    for (const s of this.videos.values()) {
      if (s.state !== 'ready') continue;
      s.value.pause();
      s.value.removeAttribute('src');
      s.value.load();
    }
    for (const url of this.videoUrls.values()) URL.revokeObjectURL(url);
    this.images.clear();
    this.blurred.clear();
    this.gifs.clear();
    this.shaders.clear();
    this.videos.clear();
    this.videoUrls.clear();
    this.listeners.clear();
  }
}

export const createTextureProvider: CreateTextureProvider = (deps) => new LazyTextureProvider(deps);
